// MARK://dhk8/admin-shell/lib/clients/payments
// Pagos digitales del negocio → dhk8-payments-gateway.
//   GET  /v1/payments/methods  → { cashEnabled, terminalEnabled, onlineEnabled }
//   PUT  /v1/payments/methods  → same
//   GET  /v1/payments/config   → PaymentConfigSummary | null   (SIN secretos)
//   PUT  /v1/payments/config   → PaymentConfigSummary
//
// CONTRACT NOTE: el servicio expone sus rutas bajo `/v1/payments/...`. El ingress
// del admin mapea `/v1/dhk8-payments-gateway/(.*)` → `/(.*)` en el pod, así que
// svcUrl('dhk8-payments-gateway', '/v1/payments/config') resuelve correcto. Las
// respuestas vienen envueltas en el sobre `{ ok, data }` → usar apiData.
//
// SECRETOS: las llaves privadas (secretKey `sk_...`, webhookSecret `whsec_...`)
// viajan en texto plano dentro de `credentials` — el servicio las cifra en reposo
// y NUNCA las devuelve. Por eso `getPaymentConfigSummary` solo trae campos no
// secretos (processor/mode/publishableKey/isActive). Para "no cambiar" un secreto,
// omitir ese campo del PUT (leave-blank-to-keep en la UI).
import { svcUrl, apiData } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'

/** Gate de métodos de pago del tenant actual (el entorno TEST/LIVE del admin ya
 *  determina qué tenant está activo). */
export interface PaymentMethods {
  cashEnabled: boolean
  terminalEnabled: boolean
  onlineEnabled: boolean
  // Fire-timing del prepago online: true = takeout prepagada entra a cocina al
  // capturarse el pago (default); false = se retiene hasta que el staff dispare.
  fireOnPrepay: boolean
}

export type PaymentProcessor = 'stripe' | 'openpay'
export type PaymentMode = 'test' | 'live'

/** Resumen NO secreto del procesador configurado (GET /v1/payments/config). */
export interface PaymentConfigSummary {
  processor: PaymentProcessor
  mode: PaymentMode
  publishableKey: string | null
  isActive: boolean
}

/** Cuerpo del PUT /v1/payments/config. Los secretos (secretKey, webhookSecret)
 *  viajan dentro de `credentials`; omitir un secreto = conservar el guardado. */
export interface SavePaymentConfigInput {
  processor: PaymentProcessor
  mode: PaymentMode
  publishableKey?: string
  merchantId?: string
  accountId?: string
  credentials: Record<string, string>
  webhookSecret?: string
  isActive?: boolean
}

// ── preview store ──────────────────────────────────────────────────────────
const db: { methods: PaymentMethods; config: PaymentConfigSummary | null } = {
  methods: { cashEnabled: true, terminalEnabled: false, onlineEnabled: false, fireOnPrepay: true },
  config: { processor: 'stripe', mode: 'test', publishableKey: 'pk_test_demo', isActive: false },
}

export async function getPaymentMethods(signal?: AbortSignal): Promise<PaymentMethods> {
  if (PREVIEW_STUB) return mock(db.methods)
  return apiData<PaymentMethods>(svcUrl('dhk8-payments-gateway', '/v1/payments/methods'), { signal })
}

export async function setPaymentMethods(m: PaymentMethods): Promise<PaymentMethods> {
  if (PREVIEW_STUB) { db.methods = { ...m }; return mock(db.methods) }
  return apiData<PaymentMethods>(svcUrl('dhk8-payments-gateway', '/v1/payments/methods'), { method: 'PUT', body: m })
}

export async function getPaymentConfigSummary(signal?: AbortSignal): Promise<PaymentConfigSummary | null> {
  if (PREVIEW_STUB) return mock(db.config)
  return apiData<PaymentConfigSummary | null>(svcUrl('dhk8-payments-gateway', '/v1/payments/config'), { signal })
}

export async function savePaymentConfig(input: SavePaymentConfigInput): Promise<PaymentConfigSummary> {
  if (PREVIEW_STUB) {
    db.config = {
      processor: input.processor,
      mode: input.mode,
      publishableKey: input.publishableKey ?? db.config?.publishableKey ?? null,
      isActive: input.isActive ?? db.config?.isActive ?? false,
    }
    return mock(db.config)
  }
  return apiData<PaymentConfigSummary>(svcUrl('dhk8-payments-gateway', '/v1/payments/config'), { method: 'PUT', body: input })
}
